<template>
    <v-col cols="12">
        <ColorSettingsDialog
            :is-open="colorSettingsDialog"
            :is-editable="isCalculationEditable"
            :side-data="currentColorSettingsSide"
            :imposition-data="imposition"
            @on-close="closeColorSettingsDialog"
            @on-save="saveColorSettings"
        />

        <MaterialsDialog
            :is-open="materialsDialog"
            :is-editable="isCalculationEditable"
            :task-data="task"
            :task-materials-data="task.calculationImpositionTaskMaterials"
            :available-materials="materials"
            @on-close="closeMaterialsDialog"
            @on-save="saveMaterials"
        />

        <SlicingFormatsDialog
            :is-open="slicingFormatsDialog"
            :product-component-data="currentProductComponent"
            @on-close="closeSlicingFormatsDialog"
            @on-save="saveSlicingFormats"
        />

        <SendingParametersDialog
            :is-open="sendingParametersDialog"
            :task-data="task"
            :delivery-methods="deliveryMethods"
            @on-close="closeSendingParametersDialog"
            @on-save="saveSendingParametersDialog"
        />
        <v-container v-if="isCuttingPaperTask" fluid class="pa-0">
            <v-row>
                <v-col v-if="isTaskParameterAvailable('machineId')" cols="12" md="6">
                    <v-autocomplete
                        v-model="task.machine"
                        :items="filterMachinesByTask(machines)"
                        label="Maszyna"
                        item-text="name"
                        return-object
                        no-data-text="Brak danych..."
                        hide-details
                        outlined
                        dense
                        :error="machineErrors(taskIndex)"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="machineChanged(imposition, task, $event)"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-autocomplete
                        :value="imposition.paperId"
                        :items="papers"
                        label="Rodzaj papieru"
                        item-value="id"
                        item-text="name"
                        no-data-text="Brak danych..."
                        hide-details
                        outlined
                        dense
                        return-object
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="changePaper(imposition, $event)"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="imposition.paperDescription"
                        label="Nazwa papieru"
                        dense
                        outlined
                        counter="256"
                        :error="paperDescriptionErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col v-if="isTaskParameterAvailable('netAmount')" cols="12" md="3">
                    <v-text-field
                        :value="task.netAmount"
                        label="Nakład netto"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('bruttoAmount')" cols="12" md="3">
                    <v-text-field
                        :value="task.bruttoAmount"
                        label="Nakład brutto"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('duration')" cols="12" md="3">
                    <v-text-field
                        :value="task.duration"
                        label="Czas"
                        suffix="min"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col v-if="isTaskParameterAvailable('wasteAmount')" cols="12" md="3">
                    <v-text-field
                        v-model="task.wasteAmount"
                        label="Straty"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('wastePercent')" cols="12" md="3">
                    <v-text-field
                        v-model="task.wastePercent"
                        label="Straty"
                        suffix="%"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col
                    v-for="(property, propertyIndex) in task.calculationImpositionTaskProperties"
                    :key="property.id"
                    cols="12"
                    md="4"
                >
                    <TaskProperty
                        :property-data="property"
                        :is-task-selected="task.isSelected"
                        :is-calculation-editable="isCalculationEditable"
                        :error="propertyErrors(propertyIndex)"
                        @input="changeTaskProperty(property, $event)"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-mask="'#####'"
                        :value="imposition.formatA"
                        label="Format A na wyjściu"
                        dense
                        outlined
                        :hint="impositionFormatHint(imposition)"
                        persistent-hint
                        :hide-details="!isCalculationEditable"
                        :error="impositionFormatAErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @input="handleFormatA(imposition, $event)"
                        @change="changeFormatA(imposition, $event)"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-mask="'#####'"
                        :value="imposition.formatB"
                        label="Format B na wyjściu"
                        dense
                        outlined
                        :hint="impositionFormatHint(imposition)"
                        persistent-hint
                        :hide-details="!isCalculationEditable"
                        :error="impositionFormatBErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @input="handleFormatB(imposition, $event)"
                        @change="changeFormatB(imposition, $event)"
                    />
                </v-col>
            </v-row>

            <v-row v-if="isTaskParameterAvailable('description')">
                <v-col cols="12">
                    <v-textarea
                        v-model="task.description"
                        label="Uwagi do procesu"
                        outlined
                        hide-details
                        auto-grow
                        rows="1"
                        dense
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
            </v-row>

            <!-- <v-row v-if="task.gangCreationAllowed">
                <ImpositionTaskGangs
                    :task="task"
                    :imposition="imposition"
                    @on-save="saveGangComponents"
                    @on-remove="removeGangComponents"
                />
            </v-row> -->
        </v-container>

        <v-container v-else-if="isPrintTask" fluid class="pa-0">
            <v-row>
                <v-col v-if="isTaskParameterAvailable('machineId')" cols="12" md="6">
                    <v-autocomplete
                        v-model="task.machine"
                        :items="filterMachinesByTask(machines)"
                        label="Maszyna"
                        item-text="name"
                        return-object
                        no-data-text="Brak danych..."
                        hide-details
                        outlined
                        dense
                        :error="machineErrors(taskIndex)"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="machineChanged(imposition, task, $event)"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4" class="pb-0">
                    <v-autocomplete
                        :value="imposition.paperId"
                        :items="papers"
                        label="Rodzaj papieru"
                        item-value="id"
                        item-text="name"
                        no-data-text="Brak danych..."
                        hide-details
                        outlined
                        dense
                        return-object
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="changePaper(imposition, $event)"
                    />
                </v-col>
                <v-col cols="12" md="6" class="pb-0">
                    <v-text-field
                        v-model="imposition.paperDescription"
                        label="Nazwa papieru"
                        dense
                        outlined
                        counter="256"
                        :error="paperDescriptionErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4" class="pt-0">
                    <v-checkbox
                        v-model="certifiedPaper"
                        label="Certyfikat"
                        :readonly="!isCalculationEditable"
                        class="mb-1 mt-0"
                        hide-details
                        :disabled="!task.isSelected"
                        @change="handleCertifiedPaperChange"
                    />
                </v-col>
                <v-col cols="12" md="4" class="pt-0">
                    <v-checkbox
                        v-model="imposition.sendPaperWaste"
                        label="Wysyłka makulatury"
                        :readonly="!isCalculationEditable"
                        class=" mb-1 mt-0"
                        hide-details
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="9" sm="9">
                    <v-autocomplete
                        ref="certificateSelect"
                        v-model="imposition.paperCertificateId"
                        :items="certificates"
                        label="Nazwa certyfikatu"
                        item-value="id"
                        item-text="name"
                        no-data-text="Brak danych..."
                        hide-details
                        outlined
                        dense
                        :error="paperCertificateErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected || !certifiedPaper"
                        @change="handleCertificateChange"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col v-if="isTaskParameterAvailable('netAmount')" cols="12" md="4">
                    <v-text-field
                        :value="task.netAmount"
                        label="Nakład netto"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('bruttoAmount')" cols="12" md="4">
                    <v-text-field
                        :value="task.bruttoAmount"
                        label="Nakład brutto"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-mask="'######'"
                        :value="imposition.extraWasteAmount"
                        label="Dodatkowe straty na zepsucie"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="changeExtraWasteAmount(imposition, $event)"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-mask="'#####'"
                        :value="imposition.formatA"
                        :label="isPrintingFromRoll ? 'Szerokość rolki' : 'Format A'"
                        suffix="mm"
                        dense
                        outlined
                        :hint="impositionWidthFormatHintFromRoll(imposition)"
                        persistent-hint
                        :hide-details="!isCalculationEditable"
                        :error="impositionFormatAErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @input="handleFormatA(imposition, $event)"
                        @change="changeFormatA(imposition, $event)"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-mask="'#####'"
                        :value="imposition.formatB"
                        :label="isPrintingFromRoll ? 'Odcięcie rolki' : 'Format B'"
                        suffix="mm"
                        dense
                        outlined
                        :hint="impositionHeightFormatHintFromRoll(imposition)"
                        persistent-hint
                        :hide-details="!isCalculationEditable"
                        :error="impositionFormatBErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @input="handleFormatB(imposition, $event)"
                        @change="changeFormatB(imposition, $event)"
                    />
                </v-col>
                <v-col v-if="isPrintingFromRoll" cols="12" md="4">
                    <v-text-field
                        v-mask="'######'"
                        :value="imposition.impositionsAmount"
                        label="Ilość montaży"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :error="impositionsAmountErrors"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="changeImpositionsAmount(imposition, $event)"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-select
                        v-model="imposition.serviceId"
                        label="Usługa"
                        :items="task.machine ? task.machine.serviceRead : []"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        :error="serviceErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="changeService"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-select
                        :value="imposition.printTypeId"
                        label="Sposób druku"
                        :items="task.machine ? task.machine.printTypeRead : []"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        :error="printTypeErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="changePrintType(imposition, $event)"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-select
                        v-model="imposition.plateTypeId"
                        label="Rodzaj płyty"
                        :items="task.machine ? applyDefaultPlateTypeSelection(task.machine.plateTypeRead) : []"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        :error="plateTypeErrors()"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected || plateDoesNotApply"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-text-field
                        :value="parseColorsValue(imposition.calculationImpositionColoursA)"
                        readonly
                        label="Kolorystyka A"
                        dense
                        outlined
                        hide-details
                        append-icon="mdi-cog"
                        :error="calculationImpositionColoursAErrors()"
                        :disabled="!task.isSelected"
                        @click="showColorSettingsDialog('A', task.machine.id)"
                        @click:append="showColorSettingsDialog('A', task.machine.id)"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-text-field
                        :value="parseColorsValue(imposition.calculationImpositionColoursB)"
                        readonly
                        label="Kolorystyka B"
                        dense
                        outlined
                        hide-details
                        append-icon="mdi-cog"
                        :placeholder="isOneSidedPrintType(imposition) ? 'bez zadruku' : ''"
                        :error="calculationImpositionColoursBErrors()"
                        :disabled="!task.isSelected || isOneSidedPrintType(imposition)"
                        @click="showColorSettingsDialog('B', task.machine.id)"
                        @click:append="showColorSettingsDialog('B', task.machine.id)"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col v-if="isTaskParameterAvailable('duration')" cols="12" md="3">
                    <v-text-field
                        :value="task.duration"
                        label="Czas"
                        suffix="min"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('wasteAmount')" cols="12" md="3">
                    <v-text-field
                        v-model="task.wasteAmount"
                        label="Straty"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('wastePercent')" cols="12" md="3">
                    <v-text-field
                        v-model="task.wastePercent"
                        label="Straty"
                        suffix="%"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-select
                        v-model="imposition.calculationColourTemplateId"
                        :items="colorTemplates"
                        label="Wzór kolorystyczny"
                        item-value="id"
                        item-text="name"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-select
                        v-model="elasticMaterial"
                        :items="filteredElasticMaterials"
                        label="Guma"
                        item-value="id"
                        item-text="name"
                        dense
                        outlined
                        return-object
                        hide-details
                        :error="elasticMaterialErrors"
                        :clearable="isCalculationEditable"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected || isElasticFieldDisabled"
                        @click:clear="clearElasticMaterial(task.calculationImpositionTaskMaterials)"
                        @change="saveElasticMaterial(task.calculationImpositionTaskMaterials, $event)"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-autocomplete
                        v-model="elasticMaterialNumber"
                        :items="rubbersRaw"
                        label="Numer gumy"
                        item-value="serialNumber"
                        item-text="serialNumber"
                        hide-details
                        outlined
                        dense
                        class="v-text-field--placeholder-dark"
                        no-data-text="Nie znaleziono"
                        clearable
                        :error="propertyErrors(0)"
                        :readonly="!isCalculationEditable"
                        :disabled="isPropertyDisabled(getElasticNumberTaskProperty)"
                        @click:clear="() => elasticMaterialNumber = null"
                        @change="changeElasticMaterialNumber()"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-autocomplete
                        v-model="imposition.rubberSourceImpositionId"
                        :items="availableRubberImpositionIds"
                        label="Wybierz gumę z montażu"
                        item-value="id"
                        item-text="ordinalNumber"
                        hide-details
                        outlined
                        dense
                        class="v-text-field--placeholder-dark"
                        clearable
                        :readonly="!isCalculationEditable"
                        :disabled="isElasticFieldDisabled || !hasNewRubber || !elasticMaterial || availableRubberImpositionIds.length === 0"
                        @click:clear="() => imposition.rubberSourceImpositionNumber = null"
                    />
                </v-col>
                <v-col
                    v-for="(property, propertyIndex) in filteredProperties"
                    :key="property.id"
                    cols="12"
                    md="4"
                >
                    <TaskProperty
                        :property-data="property"
                        :is-task-selected="task.isSelected"
                        :is-calculation-editable="isCalculationEditable"
                        :error="propertyErrors(propertyIndex)"
                        :is-disabled="isPropertyDisabled(property)"
                        @input="changeTaskProperty(property, $event)"
                    />
                </v-col>
            </v-row>

            <v-row v-if="isTaskParameterAvailable('description')">
                <v-col cols="12">
                    <v-textarea
                        v-model="task.description"
                        label="Uwagi do procesu"
                        outlined
                        hide-details
                        auto-grow
                        rows="1"
                        dense
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
            </v-row>
            <!-- <v-row v-if="task.gangCreationAllowed">
                <ImpositionTaskGangs
                    :task="task"
                    :imposition="imposition"
                    @on-save="saveGangComponents"
                    @on-remove="removeGangComponents"
                />
            </v-row> -->
        </v-container><!-- END - Print Task -->

        <v-container v-else-if="isSendingTask" fluid class="pa-0">
            <v-row>
                <v-col cols="12" md="7">
                    <v-text-field
                        :value="getDeliveryMethodName(task.deliveryMethodId)"
                        label="Transport"
                        outlined
                        dense
                        hide-details
                        readonly
                    />
                </v-col>
                <v-col cols="12" md="2">
                    <v-text-field
                        :value="getTransportDistanceTaskProperty.value"
                        item-value="name"
                        item-text="name"
                        label="Dystans"
                        :suffix="distanceSuffix"
                        outlined
                        dense
                        hide-details
                        :error="propertyErrors(1)"
                        readonly
                    />
                </v-col>
                <v-col cols="12" md="2">
                    <v-text-field
                        :value="getTransportCostTaskProperty.value"
                        item-value="name"
                        item-text="name"
                        label="Koszt"
                        outlined
                        suffix="zł"
                        dense
                        hide-details
                        :error="propertyErrors(0)"
                        readonly
                    />
                </v-col>
                <v-col cols="12" md="1">
                    <div class="d-inline-flex">
                        <v-tooltip :open-delay="tooltipDelay" top>
                            <template #activator="{on}">
                                <v-btn
                                    icon
                                    :disabled="!task.isSelected || !isCalculationEditable"
                                    v-on="on"
                                    @click="showSendingParametersDialog()"
                                >
                                    <v-icon color="blue">
                                        mdi-pencil
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Edytuj</span>
                        </v-tooltip>
                    </div>
                </v-col>
            </v-row>
            <v-row>
                <v-col v-if="isTaskParameterAvailable('description')" cols="12">
                    <v-textarea
                        v-model="task.description"
                        label="Uwagi do procesu"
                        outlined
                        hide-details
                        auto-grow
                        rows="1"
                        dense
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
            </v-row>
            <!-- <v-row v-if="task.gangCreationAllowed">
                <ImpositionTaskGangs
                    :task="task"
                    :imposition="imposition"
                    @on-save="saveGangComponents"
                    @on-remove="removeGangComponents"
                />
            </v-row> -->
        </v-container><!--END - Sending Task -->

        <v-container v-else fluid class="pa-0">
            <v-row>
                <v-col v-if="isTaskParameterAvailable('machineId')" cols="12" md="6">
                    <v-autocomplete
                        v-model="task.machine"
                        :items="filterMachinesByTask(machines)"
                        label="Maszyna"
                        item-text="name"
                        return-object
                        no-data-text="Brak danych..."
                        hide-details
                        outlined
                        dense
                        :error="machineErrors(taskIndex)"
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                        @change="machineChanged(imposition, task, $event)"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col v-if="isTaskParameterAvailable('netAmount')" cols="12" md="3">
                    <v-text-field
                        :value="task.netAmount"
                        label="Nakład netto"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('bruttoAmount')" cols="12" md="3">
                    <v-text-field
                        :value="task.bruttoAmount"
                        label="Nakład brutto"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('duration')" cols="12" md="3">
                    <v-text-field
                        :value="task.duration"
                        label="Czas"
                        suffix="min."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
            </v-row>
            <template v-if="isFormatsIncome">
                <v-row>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-mask="'#####'"
                            :value="imposition.formatA"
                            label="Format A"
                            suffix="mm"
                            dense
                            outlined
                            persistent-hint
                            hide-details
                            disabled
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-mask="'#####'"
                            :value="imposition.formatB"
                            label="Format B"
                            suffix="mm"
                            dense
                            outlined
                            persistent-hint
                            hide-details
                            disabled
                        />
                    </v-col>
                </v-row>
            </template>

            <template v-if="isSlicingAfterPrintingTask || isSlicingAfterFinishingTask || isSlicingAfterDieCuttingTask">
                <v-row v-for="(dieCutter, dieCutterIndex) in calculationImpositionDieCutters" :key="dieCutterIndex">
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="dieCutter.formatA"
                            v-mask="'#####'"
                            :label="getLabelWithIndex(dieCutterIndex, 'Format A na wyjściu')"
                            suffix="mm"
                            dense
                            outlined
                            hide-details
                            :error="impositionDieCutterFormatAErrors(dieCutterIndex)"
                            :readonly="!isCalculationEditable"
                            :disabled="!task.isSelected"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="dieCutter.formatB"
                            v-mask="'#####'"
                            :label="getLabelWithIndex(dieCutterIndex, 'Format B na wyjściu')"
                            suffix="mm"
                            dense
                            outlined
                            hide-details
                            :error="impositionDieCutterFormatBErrors(dieCutterIndex)"
                            :readonly="!isCalculationEditable"
                            :disabled="!task.isSelected"
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-text-field
                            v-model="dieCutter.partAmount"
                            v-mask="'##'"
                            label="Ilość użytków po krojeniu"
                            dense
                            outlined
                            hide-details
                            :error="impositionDieCutterPartAmountErrors(dieCutterIndex)"
                            :readonly="!isCalculationEditable"
                            :disabled="!task.isSelected"
                        />
                    </v-col>
                    <v-col cols="12" md="1">
                        <div class="d-inline-flex">
                            <v-tooltip
                                v-if="!dieCutterIndex"
                                :open-delay="tooltipDelay"
                                top
                            >
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        :disabled="calculationImpositionDieCutters.length > 1 || !task.isSelected"
                                        v-on="on"
                                        @click="addDieCutter()"
                                    >
                                        <v-icon color="green">
                                            mdi-plus
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Dodaj wykrojnik</span>
                            </v-tooltip>
                            <v-tooltip
                                v-if="dieCutterIndex > 0"
                                :open-delay="tooltipDelay"
                                top
                            >
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        :disabled="!task.isSelected"
                                        v-on="on"
                                        @click="removeDieCutter(dieCutterIndex)"
                                    >
                                        <v-icon color="red">
                                            mdi-delete
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Usuń</span>
                            </v-tooltip>
                        </div>
                    </v-col>
                </v-row>
            </template>
            <v-row v-if="isLaminationTask">
                <v-col cols="12" md="3">
                    <v-text-field
                        :value="imposition.formatA"
                        label="Format papieru A"
                        suffix="mm"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-text-field
                        :value="imposition.formatB"
                        label="Format papieru B"
                        suffix="mm"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col v-if="isTaskParameterAvailable('wasteAmount')" cols="12" md="3">
                    <v-text-field
                        v-model="task.wasteAmount"
                        label="Straty"
                        suffix="szt."
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
                <v-col v-if="isTaskParameterAvailable('wastePercent')" cols="12" md="3">
                    <v-text-field
                        v-model="task.wastePercent"
                        label="Straty"
                        suffix="%"
                        dense
                        outlined
                        hide-details
                        :readonly="!isCalculationEditable"
                        :disabled="isCalculationEditable"
                    />
                </v-col>
            </v-row>
            <template v-if="isDieCuttingTask">
                <v-row
                    v-for="(dieCutter, dieCutterIndex) in calculationImpositionDieCutters"
                    :key="dieCutterIndex + '_dieCutter'"
                >
                    <v-col cols="12" md="4">
                        <v-select
                            v-model="dieCutter.owner"
                            :items="dieCuttersOwnershipTypes"
                            :label="getLabelWithIndex(dieCutterIndex, 'Wykrojnik')"
                            item-value="id"
                            item-text="name"
                            dense
                            outlined
                            return-object
                            hide-details
                            :error="dieCutterOwnerErrors(dieCutterIndex)"
                            :clearable="isCalculationEditable"
                            :readonly="!isCalculationEditable"
                            :disabled="!task.isSelected"
                            @change="setDieCutterOwner(dieCutterIndex, $event)"
                        />
                    </v-col>
                    <v-col cols="12" md="4" class="mr-2">
                        <v-text-field
                            v-if="isNewDieCutter(dieCutterIndex)"
                            v-model="dieCutter.newDieCutterSymbol"
                            :label="getLabelWithIndex(dieCutterIndex, 'Numer Wykrojnika')"
                            dense
                            outlined
                            hide-details
                            :readonly="!isCalculationEditable"
                            :disabled="!task.isSelected"
                        />
                        <v-autocomplete
                            v-if="!isNewDieCutter(dieCutterIndex)"
                            v-model="dieCutter.dieCutter"
                            :items="dieCutters"
                            :label="getLabelWithIndex(dieCutterIndex, 'Numer Wykrojnika')"
                            item-value="id"
                            item-text="symbol"
                            no-data-text="Brak danych..."
                            dense
                            outlined
                            return-object
                            hide-details
                            :error="numberDieCutterErrors(dieCutterIndex)"
                            :clearable="isCalculationEditable"
                            :readonly="!isCalculationEditable"
                            :disabled="!task.isSelected || isDieCutterDisabled(dieCutterIndex)"
                            @change="setDieCutterType(dieCutterIndex, $event)"
                        />
                    </v-col>
                </v-row>
                <v-row
                    v-for="(dieCutterFormat, dieCutterIndexFormat) in calculationImpositionDieCutters"
                    :key="dieCutterIndexFormat + '_formats'"
                >
                    <v-col cols="12" md="4">
                        <v-text-field
                            :value="dieCutterFormat.formatA"
                            :label="getLabelWithIndex(dieCutterIndexFormat, 'Format A')"
                            suffix="mm"
                            dense
                            outlined
                            hide-details
                            disabled
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            :value="dieCutterFormat.formatB"
                            :label="getLabelWithIndex(dieCutterIndexFormat, 'Format B')"
                            suffix="mm"
                            dense
                            outlined
                            hide-details
                            disabled
                        />
                    </v-col>
                    <v-col v-if="impositionDieCutterFormatGreater(dieCutterIndexFormat) && task.isSelected" cols="12" md="4">
                        <span class="red--text text--lighten-1">
                            <b>Uwaga!</b> Zbyt duży format, dodaj proces <b>"krojenie"</b> przed procesem
                        </span>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        v-for="(dieCutterPrice, dieCutterIndexPrice) in calculationImpositionDieCutters"
                        :key="dieCutterIndexPrice"
                        cols="12"
                        md="4"
                    >
                        <v-text-field
                            :value="dieCutterPrice.price"
                            :label="getLabelWithIndex(dieCutterIndexPrice, 'Cena wykrojnika')"
                            dense
                            outlined
                            suffix="zł"
                            hide-details
                            :error="impositionDieCutterPriceErrors(dieCutterIndexPrice)"
                            :readonly="!isCalculationEditable"
                            :disabled="!task.isSelected || isDieCutterDisabled(dieCutterIndexPrice)"
                            required
                            @input="inputDieCutterPrice(dieCutterIndexPrice, $event)"
                            @change="setDieCutterPrice(dieCutterIndexPrice, $event)"
                        />
                    </v-col>
                </v-row>
            </template>
            <v-row>
                <v-col v-if="isTaskParameterAvailable('calculationImpositionTaskMaterials')" cols="12" md="6">
                    <v-text-field
                        :value="parseMaterialsValue(task.calculationImpositionTaskMaterials)"
                        readonly
                        label="Materiały"
                        dense
                        outlined
                        hide-details
                        append-icon="mdi-cog"
                        :disabled="!task.isSelected"
                        :error="materialsErrors(taskIndex)"
                        @click="showMaterialsDialog()"
                        @click:append="showMaterialsDialog()"
                    />
                </v-col>
                <v-col
                    v-for="(property, propertyIndex) in task.calculationImpositionTaskProperties"
                    :key="property.id"
                    :md="getColumnWidth"
                    cols="12"
                >
                    <TaskProperty
                        :property-data="property"
                        :is-task-selected="task.isSelected"
                        :is-calculation-editable="isCalculationEditable"
                        :error="propertyErrors(propertyIndex)"
                        @input="changeTaskProperty(property, $event)"
                    />
                </v-col>
            </v-row>

            <v-row v-if="isTaskParameterAvailable('description')">
                <v-col cols="12">
                    <v-textarea
                        v-model="task.description"
                        label="Uwagi do procesu"
                        outlined
                        hide-details
                        auto-grow
                        rows="1"
                        dense
                        :readonly="!isCalculationEditable"
                        :disabled="!task.isSelected"
                    />
                </v-col>
            </v-row>

            <!-- <v-row v-if="task.gangCreationAllowed">
                <ImpositionTaskGangs
                    :task="task"
                    :imposition="imposition"
                    @on-save="saveGangComponents"
                    @on-remove="removeGangComponents"
                />
            </v-row> -->

            <v-row v-if="isProductComponentsTableNeeded">
                <v-col cols="12">
                    <v-data-table
                        :items="task.calculationImpositionTaskCalculationProductComponents"
                        :headers="getComponentHeaders"
                        disable-pagination
                        :mobile-breakpoint="0"
                        hide-default-footer
                    >
                        <template #item.componentName="{item}">
                            <span :class="{'error--text': productComponentErrors(item)}">{{ item.calculationProductComponentName }}</span>
                        </template>
                        <template #item.outputFormatA="{item}">
                            {{ item.outputFormatA ? `${item.outputFormatA} mm` : '-' }}
                        </template>
                        <template #item.outputFormatB="{item}">
                            {{ item.outputFormatB ? `${item.outputFormatB} mm` : '-' }}
                        </template>
                        <!-- <template #item.gangs="{item}">
                            <template v-if="getItemGangsIncluded(item).length">
                                <v-btn
                                    v-for="gang in getItemGangsIncluded(item)"
                                    :key="'gung_btn_' + gang.id"
                                    class="mx-2"
                                    fab
                                    dark
                                    small
                                    color="pink"
                                    elevation="1"
                                >
                                    <strong>G{{ gang.runningNumber }} </strong>
                                </v-btn>
                            </template>
                            <span v-else>-</span>
                        </template> -->
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            :disabled="!task.isSelected"
                                            v-on="on"
                                            @click="showSlicingFormatsDialog(item)"
                                        >
                                            <v-icon color="blue">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edytuj</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>

            <v-row v-if="isTaskParameterAvailable('calculationImpositionTaskPackages')">
                <Packing
                    :is-calculation-editable="isCalculationEditable"
                    :task-data="task"
                    :products-data="calculationProducts"
                    :validations="packingErrors()"
                    @save-task-packages="saveTaskPackages"
                />
            </v-row>
        </v-container>
    </v-col>
</template>

<script>
import { appConfig } from '@/config'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { mask } from 'vue-the-mask'
import { required, requiredIf, integer, minValue, alpha, decimal, maxLength, helpers } from 'vuelidate/lib/validators'
import { twoDigitsAfterDecimal, twoDigitsAfterDecimalWithComma } from '@/utils/customValidators'
import parse from '@/mixins/parse'
import priceFormatter from '@/mixins/priceFormatter'
import tooltipDelay from '@/mixins/tooltip'

const ELASTIC_NUMBER_FIELD_ID = 5 // ID dla wlaściwości numer gumy

export default {
    name: 'ImpositionTask',
    directives: { mask },
    components: {
        ColorSettingsDialog: () => import('./ColorSettingsDialog'),
        // ImpositionTaskGangs: () => import('./Gang/impositionTaskGangs.vue'),
        MaterialsDialog: () => import('@/components/common/MaterialsDialog'),
        Packing: () => import('./Packing/Index'),
        SendingParametersDialog: () => import('./SendingParametersDialog'),
        SlicingFormatsDialog: () => import('./SlicingFormatsDialog'),
        TaskProperty: () => import('./TaskProperty'),
    },
    mixins: [ parse, tooltipDelay, priceFormatter ],
    props: {
        isCalculationEditable: {
            type: Boolean
        },
        impositionData: {
            type: Object,
            default: null
        },
        taskData: {
            type: Object,
            default: null
        },
        taskIndex: {
            type: Number,
            default: null
        },
        machines: {
            type: Array,
            default: null
        },
        papers: {
            type: Array,
            default: null
        },
        colorTemplates: {
            type: Array,
            default: null
        },
        elasticMaterials: {
            type: Array,
            default: () => []
        },
        initialTaskObject: {
            type: Object,
            default: null
        },
        initialImpositionObject: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            imposition: JSON.parse(JSON.stringify(this.impositionData)),
            currentColorSettingsSide: null,
            currentProductComponent: {},
            colorSettingsDialog: false,
            materials: [],
            elasticMaterialNumber: null,
            materialsDialog: false,
            slicingFormatsDialog: false,
            sendingParametersDialog: false,
            currentGang: {
                id: null,
                runningNumber: null,
                impositionId: null,
                formatA: null,
                formatB: null,
                partsAmount: null,
                calculationImpositionGangCalculationProductComponents: [],
            },
            currentPackage: {},
            packageDialog: false,
            boxTypes: [],
            packageTypes: [],
            actionLoading: {
                packagesDicts: false
            },
            selectedImpositionTask: {},
            expandedPanel: null,
            task: JSON.parse(JSON.stringify(this.taskData)),
            formatsHeaders: [
                {
                    text: 'Nazwa komponentu',
                    value: 'componentName',
                    align: 'left',
                    sortable: false,
                    width: 200,
                    showOnReadOnly: true
                },
                {
                    text: 'Format A wyjścia',
                    value: 'outputFormatA',
                    align: 'center',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Format B wyjścia',
                    value: 'outputFormatB',
                    align: 'center',
                    sortable: false,
                    showOnReadOnly: true
                },
            ],
            packagesHeaders: [
                {
                    text: 'Produkt',
                    value: 'productName',
                    align: 'left',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Nakład',
                    value: 'productAmount',
                    align: 'center',
                    sortable: false,
                    width: 100,
                    showOnReadOnly: true
                },
                {
                    text: 'Sposób pakowania',
                    value: 'boxType',
                    align: 'center',
                    sortable: false,
                    width: 150,
                    showOnReadOnly: true
                },
                {
                    text: 'Rodzaj opakowania',
                    value: 'packageType',
                    align: 'center',
                    sortable: false,
                    width: 180,
                    showOnReadOnly: true
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 80,
                    showOnReadOnly: true
                },
            ],
            buttons: [],
            isCertifiedPaper: this.impositionData.paperCertificateId || this.impositionData.paperCertificate?.id ? true : false,
            deliveryMethods: [],
        }
    },
    computed: {
        elasticMaterial: {
            get() {
                return this.parseElasticMaterialValue(this.task.calculationImpositionTaskMaterials)
            },
            set(val) {
                this.saveElasticMaterial(this.task.calculationImpositionTaskMaterials, val)
            }
        },
        elasticMaterialErrors() {
            return this.$v.elasticMaterial.$invalid
        },
        impositionsAmountErrors() {
            return this.$v.imposition.impositionsAmount.$invalid
        },
        isPrintingFromRoll() {
            return this.task.machine?.isPrintingFromRoll
        },
        certifiedPaper: {
            get() {
                return this.isCertifiedPaper
            },
            set(value) {
                if (!value) {
                    this.imposition.paperCertificateId = null
                }
                this.isCertifiedPaper = value

                return value
            }
        },
        getComponentHeaders() {
            let headers = [
                ...this.isCalculationEditable ? this.formatsHeaders : this.readOnlyFormatsHeaders,
                // this.task.gangCreationAllowed ? {
                //     text: 'Gangi',
                //     value: 'gangs',
                //     align: 'center',
                //     sortable: false,
                //     showOnReadOnly: true
                // } : false,
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: this.isCalculationEditable ? 80 : 120,
                    showOnReadOnly: false
                }
            ].filter(Boolean)

            return headers
        },
        dieCutters() {
            return this.$store.state.configurations.tools.dieCutters.dieCutters
        },
        certificates() {
            return this.$store.state.configurations.papers.certificates
        },
        dieCuttersOwnershipTypes() {
            return this.$store.state.configurations.tools.dieCutters.ownershipTypes
        },
        calculationProducts() {
            return this.$store.state.calculations.calculations.calculation.calculationProducts
        },
        calculationImpositionDieCutters() {
            return this.imposition.calculationImpositionDieCutters
        },
        printMachineSheetRequirements() {
            if (this.task.processId === appConfig.common.processes.printTaskId || this.task.processId === appConfig.common.processes.cuttingPaperTaskId) {
                return {
                    minSheetWidth: this.imposition.printMachine.minSheetWidth,
                    maxSheetWidth: this.imposition.printMachine.maxSheetWidth,
                    minSheetHeight: this.imposition.printMachine.minSheetHeight,
                    maxSheetHeight: this.imposition.printMachine.maxSheetHeight,
                }
            }

            return {}
        },
        dieCutterMachineSheetRequirements() {
            const dieCuttingTask = this.task.processId === appConfig.common.processes.dieCuttingTaskId

            if (!dieCuttingTask || !dieCuttingTask.machine) return

            return {
                minSheetWidth: dieCuttingTask.machine.minSheetWidth,
                maxSheetWidth: dieCuttingTask.machine.maxSheetWidth,
                minSheetHeight: dieCuttingTask.machine.minSheetHeight,
                maxSheetHeight: dieCuttingTask.machine.maxSheetHeight,
            }

        },
        readOnlyFormatsHeaders() {
            return this.formatsHeaders.filter(item => item.showOnReadOnly)
        },
        readOnlyPackageHeaders() {
            return this.packagesHeaders.filter(item => item.showOnReadOnly)
        },
        plateDoesNotApply() {
            return this.imposition.plateTypeId === 7
        },
        formatRangeValidations() {
            const result = []

            const printMachineSheetRequirements = {
                minSheetWidth: this.imposition.printMachine.minSheetWidth,
                maxSheetWidth: this.imposition.printMachine.maxSheetWidth,
                minSheetHeight: this.imposition.printMachine.minSheetHeight,
                maxSheetHeight: this.imposition.printMachine.maxSheetHeight,
            }

            result.push({
                impositionId: this.imposition.id,
                formatARangeX: this.imposition.formatA >= printMachineSheetRequirements.minSheetWidth && this.imposition.formatA <= printMachineSheetRequirements.maxSheetWidth,
                formatARangeY: this.imposition.formatA >= printMachineSheetRequirements.minSheetHeight && this.imposition.formatA <= printMachineSheetRequirements.maxSheetHeight,
                formatBRangeX: this.imposition.formatB >= printMachineSheetRequirements.minSheetWidth && this.imposition.formatB <= printMachineSheetRequirements.maxSheetWidth,
                formatBRangeY: this.imposition.formatB >= printMachineSheetRequirements.minSheetHeight && this.imposition.formatB <= printMachineSheetRequirements.maxSheetHeight
            })


            return result
        },
        formatDieCutterRangeValidations() {
            const result = []

            this.impositions.forEach(imposition => {
                if (imposition.calculationImpositionTasks.length > 0) {
                    const dieCuttingTask = imposition.calculationImpositionTasks.find(task => {
                        return task.processId === appConfig.common.processes.dieCuttingTaskId
                    })
                    const printTask = imposition.calculationImpositionTasks.find(task => {
                        return task.processId === appConfig.common.processes.printTaskId
                    })

                    imposition.calculationImpositionDieCutters.forEach((dieCutter) => {
                        let machineSheetRequirements = {}

                        if (dieCuttingTask && dieCuttingTask.isSelected && dieCuttingTask.machine) {
                            machineSheetRequirements = {
                                minSheetWidth: dieCuttingTask.machine.minSheetWidth,
                                maxSheetWidth: dieCuttingTask.machine.maxSheetWidth,
                                minSheetHeight: dieCuttingTask.machine.minSheetHeight,
                                maxSheetHeight: dieCuttingTask.machine.maxSheetHeight,
                            }
                        } else {
                            machineSheetRequirements = {
                                minSheetWidth: printTask.machine.minSheetWidth,
                                maxSheetWidth: printTask.machine.maxSheetWidth,
                                minSheetHeight: printTask.machine.minSheetHeight,
                                maxSheetHeight: printTask.machine.maxSheetHeight,
                            }
                        }
                        result.push({
                            impositionId: imposition.id,
                            dieCutterId: dieCutter.id,
                            formatARangeX: dieCutter.formatA >= machineSheetRequirements.minSheetWidth && dieCutter.formatA <= machineSheetRequirements.maxSheetWidth,
                            formatARangeY: dieCutter.formatA >= machineSheetRequirements.minSheetHeight && dieCutter.formatA <= machineSheetRequirements.maxSheetHeight,
                            formatBRangeX: dieCutter.formatB >= machineSheetRequirements.minSheetWidth && dieCutter.formatB <= machineSheetRequirements.maxSheetWidth,
                            formatBRangeY: dieCutter.formatB >= machineSheetRequirements.minSheetHeight && dieCutter.formatB <= machineSheetRequirements.maxSheetHeight
                        })
                    })
                }
            })

            return result
        },
        getColumnWidth() {
            const MD6 = 6, MD4 = 4
            const TYPESETTING_PROCESS_ID = 131 // proces składarko-sklejarka

            return this.task.processId === TYPESETTING_PROCESS_ID ? MD6 : MD4
        },
        rubbersRaw() {
            return this.$store.state.configurations.rubberRegistry.rubbersRaw
        },
        filteredProperties() {
            if (this.isPrintTask) {
                return this.task.calculationImpositionTaskProperties.filter(
                    p => p.productionProcessPropertyId !== ELASTIC_NUMBER_FIELD_ID)
            }

            return this.task.calculationImpositionTaskProperties
        },

        // Task definition
        isCTPTask() {
            return this.task.processId === appConfig.common.processes.ctpTaskId
        },
        isPrintTask() {
            return this.task.processId === appConfig.common.processes.printTaskId
        },
        isCuttingPaperTask() {
            return this.task.processId === appConfig.common.processes.cuttingPaperTaskId
        },
        isSlicingAfterPrintingTask() {
            return this.task.processId === appConfig.common.processes.slicingAfterPrintingTaskId
        },
        isSlicingAfterDieCuttingTask() {
            return this.task.processId === appConfig.common.processes.slicingAfterDieCuttingTaskId
        },
        isFinishingTask() {
            return this.task.processId === appConfig.common.processes.finishingTaskId
        },
        isSlicingAfterFinishingTask() {
            return this.task.processId === appConfig.common.processes.slicingAfterFinishingTaskId
        },
        isLaminationTask() {
            return this.task.processId === appConfig.common.processes.laminationTaskId
        },
        isSendingTask() {
            return this.task.processId === appConfig.common.processes.sendingTaskId
        },
        isDieCuttingTask() {
            return this.task.processId === appConfig.common.processes.dieCuttingTaskId
        },
        isCleaningAfterDieCuttingTask() {
            return this.task.processId === appConfig.common.processes.cleaningAfterDieCuttingTaskId
        },
        isCooperationTask() {
            return this.task.processId === appConfig.common.processes.cooperationTaskId
        },
        isFolderGluerTask() {
            return this.task.processId === appConfig.common.processes.folderGluerTaskId
        },

        isDedicatedDistantTransportType() {
            return this.task.deliveryMethodId === 8
        },
        isFormatsIncome() {
            return (
                this.isSlicingAfterPrintingTask ||
                this.isSlicingAfterFinishingTask ||
                this.isSlicingAfterDieCuttingTask
            )
        },
        isProductComponentsTableNeeded() {
            return this.isSlicingAfterPrintingTask ||
                this.isSlicingAfterFinishingTask ||
                this.isSlicingAfterDieCuttingTask ||
                this.isFinishingTask ||
                this.isDieCuttingTask ||
                this.isCleaningAfterDieCuttingTask ||
                this.isCuttingPaperTask ||
                this.isSlicingAfterPrintingTask ||
                this.isLaminationTask ||
                this.isFolderGluerTask ||
                this.isCooperationTask
        },
        getTransportTaskProperty() {
            return this.task.calculationImpositionTaskProperties.find(item => item.productionProcessPropertyId === 10)
        },
        distanceSuffix() {
            return this.isDedicatedDistantTransportType ? 'km' : ''
        },
        getTransportDistanceTaskProperty() {
            return this.task.calculationImpositionTaskProperties.find(item => item.productionProcessPropertyId === 26)
        },
        getTransportCostTaskProperty() {
            return this.task.calculationImpositionTaskProperties.find(item => item.productionProcessPropertyId === 20)
        },
        getElasticNumberTaskProperty() {
            return this.task.calculationImpositionTaskProperties.find(
                item => item.productionProcessPropertyId === ELASTIC_NUMBER_FIELD_ID
            )
        },
        getCuttingRuberMaterial() {
            return this.task.machine.serviceRead.find(item =>
                item.name === 'druk + lakier wybiórczo (wycinanie gumy)'
            ) || {
                id: null,
                name: null,
            }
        },
        getArchivedRubberMaterial() {
            return this.task.machine.serviceRead.find(item =>
                item.name === 'druk + lakier wybiórczo (zarchiwizowana guma)'
            ) || {
                id: null,
                name: null,
            }
        },
        isElasticFieldDisabled() {
            return [
                this.getCuttingRuberMaterial,
                this.getArchivedRubberMaterial,
            ].find(item => item.id === this.imposition.serviceId) === undefined
        },
        filteredElasticMaterials() {
            if (this.imposition.serviceId == this.getCuttingRuberMaterial.id) {
                return this.elasticMaterials.filter(item => item.name.includes('Nowa guma'))
            } else if (this.imposition.serviceId == this.getArchivedRubberMaterial.id) {
                return this.elasticMaterials.filter(item => item.name.includes('Stara guma'))
            }

            return this.elasticMaterials
        },
        hasNewRubber() {
            return this.filteredElasticMaterials.some(item => item.name && item.name.includes('Nowa guma'))
        },
        availableRubberImpositionIds() {
            if (this.isPrintTask) {
                return this.task.availableRubberImpositionIds
            }

            return []
        }
    },
    watch: {
        taskData: {
            handler: function(newValue) {
                this.task = JSON.parse(JSON.stringify(newValue))
            },
            deep: true
        },
        'imposition.rubberSourceImpositionId': {
            handler(newVal) {
                if (newVal === undefined) {
                    this.$set(this.imposition, 'rubberSourceImpositionId', null)
                }
            },
            immediate: true
        }
    },
    validations() {
        const impositionFormatRangeHelper = (format = null) => {
            return helpers.withParams({ type: 'impositionFormatRangeHelper' }, (value, impositionModel) => {
                const printTask = this.task.processId === appConfig.common.processes.printTaskId
                const cuttingPaperTask = this.task.processId === appConfig.common.processes.cuttingPaperTaskId

                if (printTask || cuttingPaperTask) {

                    const { formatARangeX, formatARangeY, formatBRangeX, formatBRangeY } =
                        this.formatRangeValidations.find(item => item.impositionId === impositionModel.id)
                    let isValid = false

                    if (format === 'A' && this.task.machine?.isPrintingFromRoll) {
                        isValid = formatARangeX
                    } else if (format === 'B' && this.task.machine?.isPrintingFromRoll) {
                        isValid = formatBRangeY
                    } else {
                        isValid = (formatARangeX && formatBRangeY) || (formatARangeY && formatBRangeX)
                    }

                    return this.task.isSelected ? isValid : true
                }

                return true
            })
        }

        const calculationImpositionColoursBHelper = () => {
            return helpers.withParams({ type: 'calculationImpositionColoursBHelper' }, (value, impositionModel) => {

                if (this.task.processId === appConfig.common.processes.printTaskId) {

                    const isValid = value.length > 0

                    return this.task.isSelected && impositionModel.printTypeId !== 6 ? isValid : true
                } else {
                    return true
                }
            })
        }

        const requiredImpositionFieldHelper = () => {
            return helpers.withParams({ type: 'requiredImpositionFieldHelper' }, (value) => {

                if (this.task.processId === appConfig.common.processes.printTaskId) {

                    const isValid = Array.isArray(value) ? value.length > 0 : value !== null

                    return this.task.isSelected ? isValid : true
                } else {
                    return true
                }
            })
        }

        const requiredDieCutterFieldHelper = (field) => {
            return helpers.withParams({ type: 'requiredDieCutterFieldHelper' }, (value, model) => {
                const imposition = this.imposition.calculationImpositionDieCutters.find(dieCutter => {
                    return dieCutter.id === model.id
                })


                if (!imposition) return true

                const isDieCuttingTaskSelected = this.isDieCuttingTask && this.task.isSelected


                if (!isDieCuttingTaskSelected) return true

                if (['dieCutter', 'price'].includes(field) && model.owner !== 1) {
                    return true
                }

                const validators = {
                    partAmount: required,
                    dieCutter: required,
                    owner: required,
                    formatA: required,
                    formatB: required,
                    price: [ required, twoDigitsAfterDecimalWithComma ]
                }

                return Array.isArray(validators[field]) ? validators[field].every(i => i(value)) : validators[field](value)
            })
        }

        const requiredProductComponentsHelper = () => {
            return this.task.isSelected && (this.isSlicingAfterPrintingTask || this.isSlicingAfterFinishingTask || this.isSlicingAfterDieCuttingTask)
        }

        const requiredTaskFieldHelper = (taskModel, param) => {
            if (taskModel.isSelected) {
                return taskModel.requiredFields.some(item => item.name === param)
            } else {
                return false
            }
        }

        const productsPackingHelper = () => {
            return helpers.withParams({ type: 'productsPackingHelper' }, (value, taskModel) => {
                if (taskModel.processId === appConfig.common.processes.packingTaskId) {
                    const hasPackages = taskModel.calculationImpositionTaskPackages.length > 0
                    const products = this.calculation.calculationProducts.map(item => ({
                        calculationProductId: item.id,
                        amount: item.amount
                    }))

                    const areProductsAmountsValid = products.every(product => {
                        const originalAmount = product.amount
                        let assignedAmount = 0

                        taskModel.calculationImpositionTaskPackages.forEach(item => {
                            item.calculationImpositionTaskPackageWares.forEach(ware => {
                                if (ware.calculationProductId === product.calculationProductId) {
                                    assignedAmount += parseInt(ware.amount)
                                }
                            })
                        })

                        return originalAmount === assignedAmount
                    })

                    return taskModel.isSelected ? hasPackages && areProductsAmountsValid : true
                } else {
                    return true
                }
            })
        }

        const maxWeightCapacityHelper = () => {
            return helpers.withParams({ type: 'maxWeightCapacityHelper' }, (packageModel) => {

                const maxWeightPackageCapacity = packageModel.currentMaxWeightCapacity * packageModel.packageAmount

                let waresWeight = 0

                packageModel.calculationImpositionTaskPackageWares.forEach(ware => {
                    waresWeight += ware.totalWeight
                })

                return this.task.isSelected ? (waresWeight <= maxWeightPackageCapacity && waresWeight !== 0) : true
            })
        }

        const taskPropertiesHelper = () => {
            return helpers.withParams({ type: 'taskPropertiesHelper' }, (propertyModel) => {

                const validators = {
                    required: required,
                    integer: integer,
                    alpha: alpha,
                    decimal: decimal,
                    greaterThanZero: minValue(0.01),
                    firstPairRequired: this.atLeastOnePairRequired,
                    secondPairRequired: this.atLeastOnePairRequired
                }

                const requirements = propertyModel.productionProcessProperty.validators,
                    validations = []

                requirements.forEach(requirement => {
                    if (validators[requirement.name]) {
                        const isValid = validators[requirement.name](propertyModel.value)

                        validations.push({ requirement, isValid })
                    }
                })

                return this.task.isSelected ? validations.every(item => item.isValid) : true
            })
        }

        const rules = {
            elasticMaterial: {
                required: requiredIf(function() {
                    const selectedServiceId = this.imposition.serviceId

                    return selectedServiceId === this.getCuttingRuberMaterial?.id || selectedServiceId === this.getArchivedRubberMaterial?.id
                })
            },
            imposition: {
                impositionsAmount: {
                    required: requiredIf(() => this.isPrintingFromRoll),
                    minValue: minValue(1)
                },
                paperDescription: {
                    maxLength: maxLength(256)
                },
                formatA: {
                    isValid: impositionFormatRangeHelper('A')
                },
                formatB: {
                    isValid: impositionFormatRangeHelper('B')
                },
                calculationImpositionColoursA: {
                    required: requiredImpositionFieldHelper()
                },
                calculationImpositionColoursB: {
                    required: calculationImpositionColoursBHelper()
                },
                serviceId: {
                    required: requiredImpositionFieldHelper()
                },
                printTypeId: {
                    required: requiredImpositionFieldHelper()
                },
                plateTypeId: {
                    required: requiredImpositionFieldHelper()
                },
                paperCertificateId: {
                    required: requiredIf(function() {
                        return this.certifiedPaper && this.task.isSelected
                    })
                },
                calculationImpositionDieCutters: {
                    $each: {
                        partAmount: {
                            isValid: requiredDieCutterFieldHelper('partAmount'),
                        },
                        formatA: {
                            isValid: requiredDieCutterFieldHelper('formatA'),
                        },
                        formatB: {
                            isValid: requiredDieCutterFieldHelper('formatB'),
                        },
                        dieCutter: {
                            isValid: requiredDieCutterFieldHelper('dieCutter'),
                        },
                        owner: {
                            isValid: requiredDieCutterFieldHelper('owner'),
                        },
                        price: {
                            isValid: requiredDieCutterFieldHelper('price'),
                        }
                    }
                }
            },
            task: {
                calculationImpositionTaskCalculationProductComponents: {
                    $each: {
                        outputFormatA: {
                            required: requiredIf(productComponentModel => requiredProductComponentsHelper(productComponentModel))
                        },
                        outputFormatB: {
                            required: requiredIf(productComponentModel => requiredProductComponentsHelper(productComponentModel))
                        },
                    }
                },
                calculationImpositionTaskMaterials: {
                    required: requiredIf(taskModel => requiredTaskFieldHelper(taskModel, 'calculationImpositionTaskMaterials'))
                },
                machineId: {
                    required: requiredIf(taskModel => requiredTaskFieldHelper(taskModel, 'machineId'))
                },
                calculationImpositionTaskPackages: {
                    isValid: productsPackingHelper(),
                    $each: {
                        maxWeightCapacity: maxWeightCapacityHelper()
                    }
                },
                calculationImpositionTaskProperties: {
                    $each: {
                        isValid: taskPropertiesHelper()
                    }
                }
            }
        }

        return rules
    },
    async mounted() {
        this.formattedImposition()
        if (this.isSendingTask) {
            this.getDeliveryMethods()
        }
        if (this.isPrintTask) {
            try {
                await this.$store.dispatch('configurations/getRubbersRaw')
            } catch (error) { errorHandler(error, this) }

            this.elasticMaterialNumber = this.rubbersRaw.find(
                r => r.serialNumber == this.getElasticNumberTaskProperty?.value
            ) || null
        }
    },
    methods: {
        async getDeliveryMethods() {
            return await this.$store.dispatch('shipments/shipmentsOrders/getDeliveryMethods')
                .then(response => {
                    this.deliveryMethods = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getDeliveryMethodName(id) {
            const method = this.deliveryMethods.find(item => item.id === id)

            return method ? method.name : ''
        },
        atLeastOnePairRequired() {
            let firstPairRequiredList = this.task.calculationImpositionTaskProperties.filter(
                property => property.productionProcessProperty.validators.some(validator => validator.name === 'firstPairRequired')
            )

            let secondPairRequiredList = this.task.calculationImpositionTaskProperties.filter(
                property => property.productionProcessProperty.validators.some(validator => validator.name === 'secondPairRequired')
            )

            return firstPairRequiredList.every(item => item.value) || secondPairRequiredList.every(item => item.value)
        },
        handleSelectedTask() {
            if (this.task.processId === appConfig.common.processes.printTaskId || this.task.processId === appConfig.common.processes.cuttingPaperTaskId ||
            this.task.processId === appConfig.common.processes.dieCuttingTaskId || this.isSlicingAfterFinishingTask || this.isSlicingAfterPrintingTask || this.isSlicingAfterDieCuttingTask) {
                this.$emit('update-task-on-panel-close', {task: this.task, imposition: this.imposition})
            } else {
                this.$emit('update-task-on-panel-close', {task: this.task, imposition: null})
            }

            this.$v.$reset()
        },
        handleTask(taskToGet, index, machineChanged) {
            if (this.task.processId === appConfig.common.processes.printTaskId || this.task.processId === appConfig.common.processes.cuttingPaperTaskId ||
            this.task.processId === appConfig.common.processes.dieCuttingTaskId || this.isSlicingAfterFinishingTask || this.isSlicingAfterPrintingTask || this.isSlicingAfterDieCuttingTask) {
                this.$emit('update-task-on-panel-change', {
                    initialTaskObject: this.initialTaskObject,
                    initialImpositionObject: this.initialImpositionObject,
                    task: this.task,
                    imposition: this.imposition,
                    taskToGet: taskToGet,
                    index: index,
                    machineChanged: machineChanged
                })
            } else {
                this.$emit('update-task-on-panel-change', {
                    initialTaskObject: this.initialTaskObject,
                    initialImpositionObject: this.initialImpositionObject,
                    task: this.task,
                    imposition: null,
                    taskToGet: taskToGet,
                    index: index,
                    machineChanged: machineChanged
                })
            }
            this.$v.$reset()
        },
        getLabelWithIndex(index, label) {
            return `${index + 1}. ${label}`
        },
        applyDefaultPlateTypeSelection(plateTypes) {
            if (!plateTypes || plateTypes.length === 0) {
                return plateTypes
            }

            if (plateTypes.length === 1) {
                this.imposition.plateTypeId = plateTypes[0].id
            } else if (!this.imposition.plateTypeId) {
                const defaultPlateType = plateTypes.find(type => type.default)
                
                if (defaultPlateType) {
                    this.imposition.plateTypeId = defaultPlateType.id
                }
            }

            return plateTypes
        },
        isTaskParameterAvailable(fieldName) {
            return this.task?.requiredFields.some(item => item.name === fieldName)
        },
        isOneSidedPrintType(imposition) {
            return imposition.printTypeId === 6
        },
        isDieCutterDisabled(dieCutterIndex) {
            return this.calculationImpositionDieCutters[dieCutterIndex].owner === 2 // Wykrojnik klienta
        },
        isNewDieCutter(dieCutterIndex) {
            return this.calculationImpositionDieCutters[dieCutterIndex].owner === 3 // Nowy wykrojnik
        },
        isPropertyDisabled(property) {
            return property.productionProcessProperty.id === ELASTIC_NUMBER_FIELD_ID &&
                this.getArchivedRubberMaterial.id !== this.imposition.serviceId
        },
        machineChanged(imposition, task, machine) {
            addSentryBreadcrumb('ui.action', `Zmiana maszyny na "${machine.name}"`)
            task.machineId = machine.id

            if (task.process.id === appConfig.common.processes.printTaskId) {
                imposition.printTypeId = null
                imposition.plateTypeId = null
                imposition.serviceId = null
            }
            const machineChanged = true

            this.handleTask(task, this.taskIndex, machineChanged)
        },
        changePaper(imposition, paper) {
            addSentryBreadcrumb('ui.action', `Zmiana rodzaju papieru na "${paper.name}"`)
            imposition.paper = paper
            imposition.paperId = paper.id
        },
        handleFormatA(imposition, formatA) {
            imposition.formatA = +formatA
        },
        handleFormatB(imposition, formatB) {
            imposition.formatB = +formatB
        },
        changeFormatA(imposition, formatA) {
            imposition.formatA = +formatA || 0

            imposition.calculationImpositionDieCutters.forEach(dieCutter => {
                if (!dieCutter.formatA) dieCutter.formatA = +formatA || 0
            })
            addSentryBreadcrumb('ui.action', 'Zmiana formatu A')
        },
        changeFormatB(imposition, formatB) {
            imposition.formatB = +formatB || 0

            imposition.calculationImpositionDieCutters.forEach(dieCutter => {
                if (!dieCutter.formatB) dieCutter.formatB = +formatB || 0
            })

            addSentryBreadcrumb('ui.action', 'Zmiana formatu B')
        },
        changeDieCutterPrice(dieCutterIndex, value) {
            this.calculationImpositionDieCutters[dieCutterIndex].price = this.unformatPrice(value)
        },
        changeExtraWasteAmount(imposition, value) {
            imposition.extraWasteAmount = +value || 0
            addSentryBreadcrumb('ui.action', 'Zmiana dodatkowych strat na zepsucie')
        },
        changeImpositionsAmount(imposition, value) {
            imposition.impositionsAmount = +value || 0
            addSentryBreadcrumb('ui.action', 'Zmiana ilości impozycji')
        },
        changePrintType(imposition, value) {
            imposition.printTypeId = value

            if (this.isOneSidedPrintType(imposition)) {
                imposition.calculationImpositionColoursB = []
            }

            addSentryBreadcrumb('ui.action', 'Zmiana sposobu druku')
        },
        changeService() {
            this.clearElasticMaterial(this.task.calculationImpositionTaskMaterials)
            this.getElasticNumberTaskProperty.value = null
            this.imposition.rubberSourceImpositionNumber = null
            this.elasticMaterialNumber = null
            addSentryBreadcrumb('ui.action', 'Zmiana usługi')
        },
        changeTaskProperty(property, value) {
            property.value = value
        },
        changeElasticMaterialNumber() {
            this.getElasticNumberTaskProperty.value = this.elasticMaterialNumber
        },
        formattedImposition() {
            this.imposition.calculationImpositionDieCutters.forEach(dieCutter => {
                if (!dieCutter.formatA) dieCutter.formatA = +this.imposition.formatA || 0
                if (!dieCutter.formatB) dieCutter.formatB = +this.imposition.formatB || 0
                dieCutter.price = this.formatPrice(dieCutter.price || 0)
            })
        },
        showColorSettingsDialog(side, machineId) {
            this.currentColorSettingsSide = side
            this.$store.dispatch('configurations/getMaterialsPmsTypes')
                .then(() => {
                    this.colorSettingsDialog = true
                })
            this.getPrintColours(machineId)
        },
        closeColorSettingsDialog() {
            this.currentColorSettingsSide = null
            this.colorSettingsDialog = false
        },
        saveColorSettings({ imposition, colorSettings, side }) {
            const colorKey = `calculationImpositionColours${side}`

            imposition[colorKey] = colorSettings
        },
        parseMaterialsValue(data) {
            const materials = data.map(item => item.material.name)

            return materials.length ? materials : null
        },
        getPrintColours(printMachineId) {
            this.$store.dispatch('calculations/getCalculationPrintColors', {
                query: `?machine_id=${printMachineId}`,
            })
        },
        showMaterialsDialog() {
            this.$store.dispatch('configurations/getMaterials', {
                query: `?isVisible=true&productionProcess_id=${this.task.processId}`,
                shouldSavedInStore: false
            })
                .then(response => {
                    this.materials = response.data.results
                    this.materialsDialog = true
                })
                .catch(error => errorHandler(error))
        },
        closeMaterialsDialog() {
            this.materialsDialog = false
        },
        saveMaterials(task, materials) {
            task.calculationImpositionTaskMaterials = materials
            this.task = task
        },
        saveElasticMaterial(calculationImpositionTaskMaterials, elasticMaterial) {
            if (typeof elasticMaterial !== 'undefined') {
                const index = calculationImpositionTaskMaterials.findIndex(material => material.materialTypeId === 24)
                const selectedElasticMaterial = {
                    materialId: elasticMaterial.id,
                    materialTypeId: elasticMaterial.materialTypeId,
                    value: 1
                }

                if (index > -1) {
                    calculationImpositionTaskMaterials.splice(index, 1, selectedElasticMaterial)
                } else {
                    calculationImpositionTaskMaterials.push(selectedElasticMaterial)
                }
            }
        },
        clearElasticMaterial(calculationImpositionTaskMaterials) {
            const index = calculationImpositionTaskMaterials.findIndex(material => material.materialTypeId === 24)

            calculationImpositionTaskMaterials.splice(index, 1)
        },
        parseElasticMaterialValue(calculationImpositionTaskMaterials) {
            const material = calculationImpositionTaskMaterials.find(material => material.materialTypeId === 24)

            return material?.materialId
        },
        showSlicingFormatsDialog(productComponent) {
            this.currentProductComponent = productComponent
            this.slicingFormatsDialog = true
        },
        closeSlicingFormatsDialog() {
            this.slicingFormatsDialog = false
            this.currentProductComponent = {}
        },
        saveSlicingFormats(productComponents) {
            const index = this.task.calculationImpositionTaskCalculationProductComponents.findIndex(item => item.id === productComponents.id)

            this.task.calculationImpositionTaskCalculationProductComponents.splice(index, 1, productComponents)
            this.closeSlicingFormatsDialog()
        },
        showSendingParametersDialog() {
            this.sendingParametersDialog = true
        },
        closeSendingParametersDialog() {
            this.sendingParametersDialog = false
        },
        saveSendingParametersDialog(payloadTask) {
            this.task.calculationImpositionTaskProperties.forEach((property, index) => {
                property.value = JSON.parse(JSON.stringify(payloadTask.calculationImpositionTaskProperties[index].value))
            })
            this.closeSendingParametersDialog()
        },
        saveTaskPackages(payloadTask) {
            this.task.calculationImpositionTaskPackages = JSON.parse(JSON.stringify(payloadTask.calculationImpositionTaskPackages))
        },
        filterMachinesByTask(machines) {
            return machines.filter(item => item.productionProcess.includes(this.task.processId))
        },

        impositionWidthFormatHintFromRoll(imposition) {
            if (!imposition?.printMachine?.isPrintingFromRoll) {
                return this.impositionFormatHint(imposition)
            }

            return `Dopuszczalny zakres ${imposition.printMachine.minSheetWidth}-${imposition.printMachine.maxSheetWidth}`
        },
        impositionHeightFormatHintFromRoll(imposition) {
            if (!imposition?.printMachine?.isPrintingFromRoll) {
                return this.impositionFormatHint(imposition)
            }

            return `Dopuszczalny zakres ${imposition.printMachine.minSheetHeight}-${imposition.printMachine.maxSheetHeight}`
        },

        impositionFormatHint(imposition) {
            if (!this.isCalculationEditable) return

            const printTask = imposition.calculationImpositionTasks.find(task => {
                return task.processId === appConfig.common.processes.printTaskId
            })

            if (!printTask.isSelected) return

            return `Dopuszczalny zakres ${this.printMachineSheetRequirements.minSheetWidth}-${this.printMachineSheetRequirements.maxSheetWidth} lub ${this.printMachineSheetRequirements.minSheetHeight}-${this.printMachineSheetRequirements.maxSheetHeight}`
        },
        impositionDieCutterFormatHint() {
            if (!this.isCalculationEditable) return

            const machineSheetRequirements = this.dieCutterMachineSheetRequirements || this.printMachineSheetRequirements

            return `Dopuszczalny zakres ${machineSheetRequirements.minSheetWidth}-${machineSheetRequirements.maxSheetWidth} lub ${machineSheetRequirements.minSheetHeight}-${machineSheetRequirements.maxSheetHeight}`
        },
        addDieCutter() {
            this.imposition.calculationImpositionDieCutters.push({
                calculationImposition: this.imposition.id,
                formatA: null,
                formatB: null,
                partAmount: null,
                price: this.formatPrice(0)
            })
        },
        removeDieCutter(index) {
            this.imposition.calculationImpositionDieCutters.splice(index, 1)
        },
        setDieCutterOwner(index, value) {
            this.imposition.calculationImpositionDieCutters[index].owner = value?.id
            // if diecutter type not from stock
            if (value?.id !== 1) { // if diecutter type not from stock
                this.imposition.calculationImpositionDieCutters[index].dieCutter = null
                this.imposition.calculationImpositionDieCutters[index].price = this.formatPrice(0)
            } else if (value?.id !== 3) { // if diecutter type not new
                this.imposition.calculationImpositionDieCutters[index].newDieCutterSymbol = null
            }
            this.imposition.calculationImpositionDieCutters[index].price = this.formatPrice(0)
        },
        setDieCutterType(index, value) {
            this.calculationImpositionDieCutters[index].dieCutter = value?.id
            this.$v.imposition.calculationImpositionDieCutters.$each.$iter[index].dieCutter.$touch()
        },
        setDieCutterPrice(index, value) {
            let formattedValue = Number(value.replace(',', '.'))

            if (!isNaN(formattedValue)) {
                this.calculationImpositionDieCutters[index].price = formattedValue.toFixed(2).replace('.', ',')
            } else {
                this.calculationImpositionDieCutters[index].price = value
            }
            this.$forceUpdate()
        },
        inputDieCutterPrice(index, value) {
            if ((!twoDigitsAfterDecimalWithComma(value) && !twoDigitsAfterDecimal(value)) || value === undefined || value === null || value === '') {
                this.calculationImpositionDieCutters[index].price = value
            } else {
                let formattedValue = Number(value).toString().replace('.', ',')

                this.calculationImpositionDieCutters[index].price = isNaN(formattedValue) ? value : formattedValue
            }
            this.$forceUpdate()
        },
        // Gangi
        saveGangComponents(gang) {
            if (gang.id) {
                const updatedGangIndex = this.task.gangsOut.findIndex(g => g.id === gang.id)

                this.task.gangsOut.splice(updatedGangIndex, 1, gang)
            } else {
                this.task.gangsOut.push(gang)
            }

            this.handleTask(this.task, this.taskIndex, true)
        },
        removeGangComponents(gangId) {
            const index = this.task.gangsOut.findIndex(gang => gang.id === gangId)

            this.task.gangsOut.splice(index, 1)

            this.handleTask(this.task, this.taskIndex, true)
        },
        getItemGangsIncluded(item) {
            const itemGangs = this.task.gangsOut.filter(gang => gang.impositionGangProductComponents.some(
                component => component.id === item.calculationProductComponent
            ))

            return itemGangs
        },
        // Walidacje
        taskErrors() {
            if (!this.isCalculationEditable) return

            let errorsCount = 0

            const printTaskId = appConfig.common.processes.printTaskId,
                cuttingPaperTaskId = appConfig.common.processes.cuttingPaperTaskId

            const isPrintTaskSelected = this.task.processId === printTaskId && this.task.isSelected
            const isCuttingPaperTaskSelected = this.task.processId === cuttingPaperTaskId && this.task.isSelected

            // Druk
            if (isPrintTaskSelected && (
                this.$v.imposition.paperDescription.$invalid ||
                this.$v.imposition.formatA.$invalid ||
                this.$v.imposition.formatB.$invalid ||
                this.$v.imposition.calculationImpositionColoursA.$invalid ||
                this.$v.imposition.calculationImpositionColoursB.$invalid ||
                this.$v.imposition.serviceId.$invalid ||
                this.$v.imposition.printTypeId.$invalid ||
                this.$v.imposition.plateTypeId.$invalid ||
                this.$v.imposition.paperCertificateId.$invalid
            )) {
                errorsCount++
            }

            // Wykrojniki
            if (
                (
                    this.isSlicingAfterPrintingTask ||
                    this.isSlicingAfterFinishingTask
                )
                && this.task.isSelected
            ) {
                Object.values(this.$v.imposition.calculationImpositionDieCutters.$each.$iter).forEach(item => {
                    item.formatA.$invalid && errorsCount++
                    item.formatB.$invalid && errorsCount++
                    item.partAmount.$invalid && errorsCount++
                })
            }
            if (this.isDieCuttingTask && this.task.isSelected) {
                Object.values(this.$v.imposition.calculationImpositionDieCutters.$each.$iter).forEach(item => {
                    item.dieCutter.$invalid && errorsCount++
                    item.owner.$invalid && errorsCount++
                    item.price.$invalid && errorsCount++
                })
            }

            // Cutting Paper before Printing task
            if (isCuttingPaperTaskSelected && (
                this.$v.imposition.paperDescription.$invalid ||
                this.$v.imposition.formatA.$invalid ||
                this.$v.imposition.formatB.$invalid
            )) {
                errorsCount++
            }

            if (this.$v.task.$invalid) {
                errorsCount++
            }

            return errorsCount > 0

        },
        productComponentErrors(productComponent) {
            if (!this.isCalculationEditable) return

            const productComponentIndex = this.$v.task.$model.calculationImpositionTaskCalculationProductComponents.findIndex(item => item.id === productComponent.id)

            return this.$v.task.calculationImpositionTaskCalculationProductComponents.$each.$iter[productComponentIndex].$invalid
        },
        paperDescriptionErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.paperDescription.$invalid
        },
        impositionFormatAErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.formatA.$invalid
        },
        impositionFormatBErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.formatB.$invalid
        },
        impositionDieCutterPriceErrors(index) {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionDieCutters.$each[index].price.$invalid
        },
        impositionDieCutterFormatAErrors(index) {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionDieCutters.$each[index].formatA.$invalid
        },
        impositionDieCutterFormatBErrors(index) {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionDieCutters.$each[index].formatB.$invalid
        },
        impositionDieCutterFormatGreater(index) {
            return this.impositionDieCutterFormatBErrors(index) || this.impositionDieCutterFormatAErrors(index)
        },
        impositionDieCutterPartAmountErrors(index) {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionDieCutters.$each[index].partAmount.$invalid
        },
        numberDieCutterErrors(index) {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionDieCutters.$each[index].dieCutter.$invalid
        },
        dieCutterOwnerErrors(index) {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionDieCutters.$each[index].owner.$invalid
        },
        calculationImpositionColoursAErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionColoursA.$invalid
        },
        calculationImpositionColoursBErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.calculationImpositionColoursB.$invalid
        },
        machineErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.task.machineId.$invalid
        },
        paperCertificateErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.paperCertificateId.$invalid
        },
        propertyErrors(propertyIndex) {
            if (!this.isCalculationEditable) return

            return this.$v.task.calculationImpositionTaskProperties.$each[propertyIndex].$invalid
        },
        materialsErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.task.calculationImpositionTaskMaterials.$invalid
        },
        serviceErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.serviceId.$invalid
        },
        plateTypeErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.plateTypeId.$invalid
        },
        printTypeErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.imposition.printTypeId.$invalid
        },
        packingErrors() {
            if (!this.isCalculationEditable) return

            return this.$v.task.calculationImpositionTaskPackages
        },
        handleCertificateChange(certificateId) {
            this.imposition.paperCertificateId = certificateId
        },
        handleCertifiedPaperChange(checked) {
            if (!checked) {
                this.imposition.paperCertificate = null
                this.imposition.paperCertificateId = null
                this.$refs.certificateSelect.internalValue = null
            }
        }
    }
}
</script>
